import { Group, MantineSize, Table, TableThProps, Tooltip, UnstyledButton } from "@mantine/core";

import { IconArrowsSort, IconSortAscending, IconSortDescending } from "@tabler/icons-react";

import { TableSortState } from "@/billing/utils/table-sort";
import { TextWrapper } from "@/components/TextWrapper";
import { SortableThDirection } from "@/constants/sortable-th";

const ICON_STYLE = { minWidth: 16, maxWidth: 16 };

interface BillingSortableThProps<TColumnKey extends string> extends TableThProps {
  label: React.ReactNode;
  /** The column's sort key, or undefined when its sort is still waiting on a data layer. */
  columnKey: TColumnKey | undefined;
  sortState: TableSortState<TColumnKey> | undefined;
  onSort: (columnKey: TColumnKey) => void;
  /** Test id for the header's sort control. */
  testId: string;
  /** Label font size, matching whatever the surrounding table's headers use. */
  labelSize?: MantineSize;
  /**
   * Shows the neutral sort affordance behind this tooltip and disables sorting. Use for a column
   * whose data is not available yet, so the header reads as sortable-to-come rather than as a
   * column that will never sort.
   */
  pendingTooltip?: string;
}

function getSortDirection<TColumnKey extends string>(
  columnKey: TColumnKey | undefined,
  sortState: TableSortState<TColumnKey> | undefined,
): SortableThDirection {
  if (!columnKey || sortState?.columnKey !== columnKey) return SortableThDirection.None;
  return sortState.direction === "asc" ? SortableThDirection.Asc : SortableThDirection.Desc;
}

function getAriaSort(sortDirection: SortableThDirection): "ascending" | "descending" | undefined {
  if (sortDirection === SortableThDirection.Asc) return "ascending";
  if (sortDirection === SortableThDirection.Desc) return "descending";
  return undefined;
}

function SortIcon({ sortDirection }: { sortDirection: SortableThDirection }) {
  if (sortDirection === SortableThDirection.Asc) {
    return <IconSortAscending aria-hidden style={ICON_STYLE} color="var(--mantine-color-dark-9)" />;
  }
  if (sortDirection === SortableThDirection.Desc) {
    return (
      <IconSortDescending aria-hidden style={ICON_STYLE} color="var(--mantine-color-dark-9)" />
    );
  }
  return <IconArrowsSort aria-hidden style={ICON_STYLE} color="var(--mantine-color-gray-6)" />;
}

/**
 * A click-to-toggle sort header shared by the billing tables. The first click on a column sorts
 * ascending; clicking the active column again reverses it.
 *
 * Passing `pendingTooltip` with no `columnKey` renders the same header behind a tooltip, showing the
 * neutral sort icon and ignoring clicks.
 */
export default function BillingSortableTh<TColumnKey extends string>({
  label,
  columnKey,
  sortState,
  onSort,
  testId,
  labelSize,
  pendingTooltip,
  ...thProps
}: BillingSortableThProps<TColumnKey>) {
  const sortDirection = getSortDirection(columnKey, sortState);

  const control = (
    <UnstyledButton
      w="100%"
      data-testid={testId}
      aria-disabled={pendingTooltip ? true : undefined}
      onClick={columnKey ? () => onSort(columnKey) : undefined}
      aria-label={typeof label === "string" ? `Sort by ${label}` : undefined}
    >
      <Group gap="xs" wrap="nowrap" align="center">
        <TextWrapper c="dark.9" variant="bold" size={labelSize}>
          {label}
        </TextWrapper>
        <SortIcon sortDirection={sortDirection} />
      </Group>
    </UnstyledButton>
  );

  return (
    <Table.Th {...thProps} aria-sort={getAriaSort(sortDirection)}>
      {pendingTooltip ? <Tooltip label={pendingTooltip}>{control}</Tooltip> : control}
    </Table.Th>
  );
}
